/**
 * Raport zarządu — teksty sekcji sales (`mbr.sales.*`). WŁAŚCICIEL: agent sekcji sales (docs/RAPORT-ZARZADU.md).
 * Podpięte już w i18n/mbr.ts — dopisuj klucze tutaj, nie w extra.ts. `en` typowane względem `pl`.
 * Własne kody uzgodnień sekcji: `checks: { KOD: '…' }` (panel szuka `mbr.checks.KOD`, potem `mbr.sales.checks.KOD`).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrSalesPl = {
  title: 'Sprzedaż i rentowność',
  description: 'Sprzedaż po korektach, marża, trasy, udział oddziałów, próg rentowności i wynik oddziałów.',
  kpi: {
    netSales: 'Sprzedaż po korektach',
    orders: 'Zamówienia',
    ordersPerDeliveryDay: 'Zamówienia na dzień dostaw',
    grossMarginPct: 'Marża brutto',
    profitPerOrder: 'Zysk na zamówienie',
    cogs: 'Koszt towaru',
    grossProfit: 'Zysk ze sprzedaży',
    corrections: 'Korekty',
    correctionsPct: 'Korekty, % sprzedaży',
    operatingResult: 'Wynik operacyjny',
    distanceToBep: 'Dystans do progu rentowności',
    contributionMarginPct: 'Marża kontrybucji',
    bep: 'Próg rentowności (BEP)',
    pctAboveBep: 'Sprzedaż względem progu',
  },
  notes: {
    costProvisional: 'Koszt magazynowy, do potwierdzenia',
  },
  checks: {
    ROUTE_GROUP_UNMAPPED:
      'Grupy tras bez oddziału ({groups}): {actual} sprzedaży nie trafia do żadnego oddziału. Uzupełnij mapowanie tras w ustawieniach raportu.',
  },
  sources: {
    label: 'Źródło: sprzedaż — {totals}, trasy — {routes}',
    ERP: 'dokumenty ERP (faktury i korekty)',
    SALES_DAILY: 'zestawienie dzienne (bez rozbicia korekt)',
    IMPORT: 'import zestawienia',
    MANUAL: 'wpis ręczny',
    MIXED: 'kilka źródeł',
    MISSING: 'brak danych',
    costProvisional:
      'Koszt towaru i marża: koszt magazynowy z dokumentów ERP — wartość robocza, do potwierdzenia przez finanse.',
  },
  restricted:
    'Wynik, próg rentowności i wynik oddziałów widzi zarząd (uprawnienie do danych finansowych raportu).',
  noCosts:
    'Brak kosztów za ten okres — wynik i próg rentowności pojawią się po wprowadzeniu kosztów w zamknięciu miesiąca.',
  branchNames: { GD: 'Gdańsk (GD)', ZK: 'Koszalin (ZK)', CT: 'Toruń (CT)' },
  ai: {
    subtitle:
      'Dźwignie progu rentowności, przyczyny zmiany wyniku, trasy i oddziały — liczby wyłącznie z danych raportu',
  },
  routes: {
    title: 'Sprzedaż wg tras',
    description:
      'Od największej grupy tras; udział liczony do sprzedaży całej firmy. Wykres i tabela w tej samej kolejności.',
    group: 'Grupa tras',
    branch: 'Oddział',
    current: 'Bieżący',
    base: 'Bazowy',
    change: 'Zmiana',
    share: 'Udział',
    total: 'Suma tras',
    pickup: 'Odbiór osobisty',
    noBranch: 'bez oddziału',
    empty: 'Brak sprzedaży z przypisaną trasą w tym okresie.',
    shareBase: 'bazowo {value}',
    aiLead: 'największy wkład',
    aiAgainst: 'w przeciwną stronę',
    aiLeadHint: 'Wniosek AI: ta grupa tras najbardziej zmieniła sprzedaż względem okresu bazowego.',
    aiAgainstHint: 'Wniosek AI: ta grupa tras zmieniła sprzedaż w przeciwną stronę niż cała firma.',
    aiLegend: 'grupy tras wskazane we wnioskach AI',
  },
  branches: {
    title: 'Oddziały',
    description: 'Sprzedaż oddziału i jej udział w sprzedaży całej firmy (nie Gdańska).',
    descriptionResult:
      'Sprzedaż, udział w całej firmie i wynik oddziału po kosztach bezpośrednich i narzucie centrali.',
    shareBase: 'udział w okresie bazowym: {value}',
    ofCompany: '{value} firmy',
    details: 'Szczegóły w tabeli wyniku oddziałów',
  },
  bridge: {
    title: 'Most wyniku',
    description:
      'Od sprzedaży do wyniku operacyjnego: koszt towaru, koszty zmienne, koszty stałe z wynagrodzeniami.',
    steps: {
      sales: 'Sprzedaż',
      cogs: 'Towar',
      variable: 'Koszty zmienne',
      fixed: 'Stałe + wynagrodzenia',
      result: 'Wynik',
    },
    value: 'Kwota',
    pctOfSales: '% sprzedaży',
    level: 'Poziom po kroku',
    base: 'Okres bazowy',
    legend: { sales: 'Sprzedaż', costs: 'Koszty', result: 'Wynik operacyjny' },
    drivers: 'Co zmieniło wynik względem okresu bazowego',
    driversHint: 'Cztery efekty sumują się dokładnie do zmiany wyniku (wniosek AI z danych raportu).',
    driver: {
      volume: 'Wolumen sprzedaży',
      margin: 'Marża brutto %',
      variable: 'Koszty zmienne %',
      fixed: 'Stałe + wynagrodzenia',
    },
    noDriver: 'Zmiana wyniku względem okresu bazowego jest poniżej progu istotności z ustawień raportu.',
  },
  bep: {
    title: 'Próg rentowności',
    cm: 'Marża kontrybucji',
    bep: 'Próg rentowności (sprzedaż)',
    result: 'Wynik operacyjny',
    gap: 'Brakuje do progu',
    surplus: 'Nadwyżka ponad próg',
    position: 'Sprzedaż {pct} {side} progu',
    above: 'powyżej',
    below: 'poniżej',
    none: 'Marża kontrybucji ≤ 0: każda złotówka sprzedaży powiększa stratę, progu nie ma.',
    formula:
      'BEP = (koszty stałe + wynagrodzenia) ÷ marża kontrybucji; marża kontrybucji = 1 − towar % − koszty zmienne %.',
  },
  simulator: {
    title: 'Symulator progu rentowności',
    description:
      'Co jeśli: suwaki startują od danych okresu. Liczone tym samym wzorem co raport, nic nie zapisuje się samo.',
    sales: 'Sprzedaż miesięczna',
    grossMargin: 'Marża brutto',
    variable: 'Koszty zmienne, % sprzedaży',
    fixed: 'Koszty stałe + wynagrodzenia',
    reset: 'Wróć do danych okresu',
    result: 'Wynik operacyjny miesięcznie',
    ofBep: '{pct} progu',
    toBep: 'Próg {bep} — brakuje {gap} sprzedaży.',
    overBep: 'Próg {bep} — nadwyżka {gap} ponad próg.',
    noData: 'Brak danych okresu do startu symulatora (sprzedaż lub koszty).',
    save: 'Zapisz scenariusz',
    namePlaceholder: 'Nazwa scenariusza, np. „Koszty zmienne jak w styczniu”',
    shared: 'Widoczny dla zarządu',
    scenarios: 'Zapisane scenariusze',
    scenariosEmpty: 'Brak zapisanych scenariuszy.',
    load: 'Wczytaj',
    delete: 'Usuń',
    by: '{author} · {date}',
    private: 'prywatny',
    saveError: 'Nie udało się zapisać scenariusza.',
    apply: 'Zastosuj sugestię AI',
    applied: 'Sugestia AI w suwakach',
    levers: 'Dźwignie wyniku — co daje każdy krok miesięcznie',
    leversHint: 'Liczone tym samym wzorem co symulator, od danych okresu.',
    startValue: 'dane okresu: {value}',
    vsStart: '{value} względem danych okresu',
    inSliders: 'W suwakach',
    short: {
      sales: 'Sprzedaż',
      margin: 'Marża brutto',
      variable: 'Koszty zmienne',
      fixed: 'Stałe + wynagr.',
    },
  },
  branchResults: {
    title: 'Wynik oddziałów',
    description:
      'Marża oddziału minus koszty bezpośrednie (pojazdy, powierzchnie, energia, transport, wynagrodzenia oddziału) i narzut centrali.',
    branch: 'Oddział',
    sales: 'Sprzedaż',
    grossMargin: 'Marża brutto',
    direct: 'Koszty bezpośr.',
    directPct: '% sprzedaży',
    allocation: 'Klucz',
    hq: 'Narzut centrali',
    result: 'Wynik',
    key: 'Klucz podziału kosztów centrali: {key}',
    keys: { SALES: 'udział w sprzedaży', ORDERS: 'udział w zamówieniach', KM: 'udział w kilometrach' },
    fallback: 'Brak danych do klucza „{requested}” — użyto udziału w sprzedaży.',
    hqPool: 'Koszty centrali do podziału: {value}',
    unassigned: 'Marża poza oddziałami (np. odbiór osobisty): {value}',
    estimated: 'Marża części oddziałów szacowana marżą firmy (źródło tras bez kosztu towaru).',
    empty: 'Brak danych tras w tym okresie.',
  },
  trend: {
    title: 'Trend 12 miesięcy',
    description: 'Sprzedaż po korektach (słupki) i marża brutto (linia).',
    netSales: 'Sprzedaż po korektach',
    margin: 'Marża brutto',
    currentMonth: 'Sprzedaż — miesiąc raportu',
    earlier: 'Sprzedaż — poprzednie miesiące',
  },
  compareTitle: 'Wszystkie wskaźniki sekcji',
  compareHint: '{count} wskaźników: bieżący, bazowy i zmiana.',
  compareShow: 'Pokaż tabelę',
  compareHide: 'Zwiń tabelę',
};

export const mbrSalesEn: Shape<typeof mbrSalesPl> = {
  title: 'Sales & profitability',
  description: 'Net sales after corrections, margin, routes, branch shares, break-even and branch results.',
  kpi: {
    netSales: 'Net sales after corrections',
    orders: 'Orders',
    ordersPerDeliveryDay: 'Orders per delivery day',
    grossMarginPct: 'Gross margin',
    profitPerOrder: 'Profit per order',
    cogs: 'Cost of goods',
    grossProfit: 'Gross profit',
    corrections: 'Corrections',
    correctionsPct: 'Corrections, % of sales',
    operatingResult: 'Operating result',
    distanceToBep: 'Distance to break-even',
    contributionMarginPct: 'Contribution margin',
    bep: 'Break-even point (BEP)',
    pctAboveBep: 'Sales vs break-even',
  },
  notes: {
    costProvisional: 'Warehouse cost, to be confirmed',
  },
  checks: {
    ROUTE_GROUP_UNMAPPED:
      'Route groups without a branch ({groups}): {actual} of sales reach no branch. Complete the route mapping in the report settings.',
  },
  sources: {
    label: 'Source: sales — {totals}, routes — {routes}',
    ERP: 'ERP documents (invoices and corrections)',
    SALES_DAILY: 'daily summary (no split of corrections)',
    IMPORT: 'imported summary',
    MANUAL: 'manual entry',
    MIXED: 'several sources',
    MISSING: 'no data',
    costProvisional:
      'Cost of goods and margin: warehouse cost from ERP documents — a working figure, to be confirmed by finance.',
  },
  restricted: 'Result, break-even and branch results are visible to the board (report finance permission).',
  noCosts:
    'No costs for this period — result and break-even appear once costs are entered in the month close.',
  branchNames: { GD: 'Gdańsk (GD)', ZK: 'Koszalin (ZK)', CT: 'Toruń (CT)' },
  ai: {
    subtitle:
      'Break-even levers, drivers of the result change, routes and branches — numbers only from report data',
  },
  routes: {
    title: 'Sales by route',
    description:
      'Biggest route group first; share of the whole company sales. Chart and table in the same order.',
    group: 'Route group',
    branch: 'Branch',
    current: 'Current',
    base: 'Base',
    change: 'Change',
    share: 'Share',
    total: 'Routes total',
    pickup: 'Customer pickup',
    noBranch: 'no branch',
    empty: 'No sales with a route in this period.',
    shareBase: 'base {value}',
    aiLead: 'biggest contribution',
    aiAgainst: 'opposite direction',
    aiLeadHint: 'AI insight: this route group changed sales the most against the base period.',
    aiAgainstHint: 'AI insight: this route group moved sales the other way than the whole company.',
    aiLegend: 'route groups named in the AI insights',
  },
  branches: {
    title: 'Branches',
    description: 'Branch sales and their share of the whole company sales (not of Gdańsk).',
    descriptionResult:
      'Sales, share of the whole company and branch result after direct costs and head-office overhead.',
    shareBase: 'share in the base period: {value}',
    ofCompany: '{value} of company',
    details: 'Details in the branch results table',
  },
  bridge: {
    title: 'Result bridge',
    description: 'From sales to operating result: cost of goods, variable costs, fixed costs with payroll.',
    steps: {
      sales: 'Sales',
      cogs: 'Goods',
      variable: 'Variable costs',
      fixed: 'Fixed + payroll',
      result: 'Result',
    },
    value: 'Amount',
    pctOfSales: '% of sales',
    level: 'Level after step',
    base: 'Base period',
    legend: { sales: 'Sales', costs: 'Costs', result: 'Operating result' },
    drivers: 'What changed the result vs the base period',
    driversHint: 'The four effects add up exactly to the result change (AI insight from report data).',
    driver: {
      volume: 'Sales volume',
      margin: 'Gross margin %',
      variable: 'Variable costs %',
      fixed: 'Fixed + payroll',
    },
    noDriver:
      'The result change vs the base period is below the significance threshold of the report settings.',
  },
  bep: {
    title: 'Break-even',
    cm: 'Contribution margin',
    bep: 'Break-even sales',
    result: 'Operating result',
    gap: 'Missing to break-even',
    surplus: 'Surplus over break-even',
    position: 'Sales {pct} {side} break-even',
    above: 'above',
    below: 'below',
    none: 'Contribution margin ≤ 0: every zloty of sales deepens the loss, there is no break-even.',
    formula:
      'BEP = (fixed costs + payroll) ÷ contribution margin; contribution margin = 1 − goods % − variable costs %.',
  },
  simulator: {
    title: 'Break-even simulator',
    description:
      'What if: sliders start from the period data. Same formula as the report; nothing is saved automatically.',
    sales: 'Monthly sales',
    grossMargin: 'Gross margin',
    variable: 'Variable costs, % of sales',
    fixed: 'Fixed costs + payroll',
    reset: 'Back to period data',
    result: 'Monthly operating result',
    ofBep: '{pct} of break-even',
    toBep: 'Break-even {bep} — {gap} of sales missing.',
    overBep: 'Break-even {bep} — {gap} over break-even.',
    noData: 'No period data to start the simulator (sales or costs).',
    save: 'Save scenario',
    namePlaceholder: 'Scenario name, e.g. “Variable costs as in January”',
    shared: 'Visible to the board',
    scenarios: 'Saved scenarios',
    scenariosEmpty: 'No saved scenarios.',
    load: 'Load',
    delete: 'Delete',
    by: '{author} · {date}',
    private: 'private',
    saveError: 'Could not save the scenario.',
    apply: 'Apply AI suggestion',
    applied: 'AI suggestion in the sliders',
    levers: 'Result levers — what each step gives per month',
    leversHint: 'Computed with the same formula as the simulator, from the period data.',
    startValue: 'period data: {value}',
    vsStart: '{value} vs period data',
    inSliders: 'In the sliders',
    short: { sales: 'Sales', margin: 'Gross margin', variable: 'Variable costs', fixed: 'Fixed + payroll' },
  },
  branchResults: {
    title: 'Branch results',
    description:
      'Branch margin minus direct costs (vehicles, premises, energy, transport, branch payroll) and head-office overhead.',
    branch: 'Branch',
    sales: 'Sales',
    grossMargin: 'Gross margin',
    direct: 'Direct costs',
    directPct: '% of sales',
    allocation: 'Key',
    hq: 'Head-office overhead',
    result: 'Result',
    key: 'Head-office allocation key: {key}',
    keys: { SALES: 'share of sales', ORDERS: 'share of orders', KM: 'share of kilometres' },
    fallback: 'No data for the “{requested}” key — share of sales used instead.',
    hqPool: 'Head-office costs to allocate: {value}',
    unassigned: 'Margin outside branches (e.g. customer pickup): {value}',
    estimated:
      'Margin of some branches estimated with the company margin (route source without cost of goods).',
    empty: 'No route data in this period.',
  },
  trend: {
    title: '12-month trend',
    description: 'Net sales after corrections (bars) and gross margin (line).',
    netSales: 'Net sales after corrections',
    margin: 'Gross margin',
    currentMonth: 'Sales — report month',
    earlier: 'Sales — previous months',
  },
  compareTitle: 'All indicators of the section',
  compareHint: '{count} indicators: current, base and change.',
  compareShow: 'Show table',
  compareHide: 'Collapse table',
};
